<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { nextTick, ref, watch } from 'vue';

import AppButton from '@/components/ui/AppButton.vue';
import { useConfirmStore } from '@/stores/confirm';

const confirm = useConfirmStore();
const cancelButton = ref<InstanceType<typeof AppButton> | null>(null);
let returnFocusTo: HTMLElement | null = null;

// Cancel takes the focus rather than confirm. The dialog is only ever raised over a
// deletion, so a stray Enter must not be the thing that carries one out.
watch(
  () => confirm.pending,
  async (pending) => {
    if (!pending) {
      returnFocusTo?.focus();
      returnFocusTo = null;
      return;
    }
    returnFocusTo = document.activeElement as HTMLElement | null;
    await nextTick();
    (cancelButton.value?.$el as HTMLElement | undefined)?.focus();
  },
);

onKeyStroke('Escape', () => {
  if (confirm.pending) confirm.answer(false);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-from-class="opacity-0"
      enter-active-class="transition duration-100"
      leave-to-class="opacity-0"
      leave-active-class="transition duration-100"
    >
      <div
        v-if="confirm.pending"
        class="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4"
        @click.self="confirm.answer(false)"
      >
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          class="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
        >
          <h2 id="confirm-dialog-title" class="text-sm font-semibold text-stone-900">
            {{ confirm.pending.title }}
          </h2>
          <p v-if="confirm.pending.body" class="mt-2 text-sm text-stone-600">
            {{ confirm.pending.body }}
          </p>

          <div class="mt-5 flex justify-end gap-2">
            <AppButton ref="cancelButton" size="sm" @click="confirm.answer(false)">
              {{ $t('confirm.cancel') }}
            </AppButton>
            <AppButton size="sm" variant="danger" @click="confirm.answer(true)">
              {{ confirm.pending.confirmLabel ?? $t('confirm.delete') }}
            </AppButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
